import React from 'react';
import { ReactComponent as InstagramIcon } from '@images/instagram.svg';
import { ReactComponent as FacebookIcon } from '@images/facebook.svg';
import { ReactComponent as PinterestIcon } from '@images/pinterest.svg';
import { ReactComponent as ClassicJoyLogoSvg } from '@images/classic-joy-logo.svg';
import styled from 'styled-components';
import { themeColor } from '@utils/styledTheme.util';
import { Button } from '@components/Button';
import { CTALink } from '@components/CTALink';
import { Flex } from '@components/Flex';
import { Columns } from '@components/Columns';
import { variant } from 'styled-system';
import { getRootUrl } from '@utils/urlHelpers.util';

export const ClassicFooterHeroMenuItem = styled.div`
  // This is not a joykit typography so leaving it here
  a {
    font-family: Austin News Headline Web;
    font-style: normal;
    font-weight: 300;
    font-size: 48px;
    line-height: 64px;
    letter-spacing: -0.015em;
    white-space: nowrap;
    @media screen and (min-width: ${props => props.theme.breakpoints[1]}) {
      font-size: 72px;
      line-height: 93px;
    }
  }
`;

export const FooterSubHeroHeader = styled.div.attrs({ typography: 'sectionEyeBrow24' })`
  ${variant({
    prop: 'typography',
    scale: 'typography',
  })};
  margin-bottom: 14px;

  color: ${themeColor('linkFooterDefault')};
`;

export const FooterSubHeroItem = styled.div`
  ${CTALink} {
    line-height: 18px;
  }
  margin-bottom: 21px;
`;

const appGetStartedMenuContent = () => {
  return (
    <>
      <Button id="footer2create" href={getRootUrl('/createwedding')} forwardedAs={'a'} className="get-started-button" variant="secondary">
        Get Started
      </Button>
      <Button
        id="footer2login"
        href={getRootUrl('/login')}
        forwardedAs={'a'}
        className="bottom-footer-link footer-login-button"
        variant="altPurpleSecondary"
      >
        Log In
      </Button>
      <CTALink id="footer2login" href={getRootUrl('/login')} className="bottom-footer-link footer-login-link" variant="altPurple">
        Log In
      </CTALink>
      <CTALink id="footer2find" href={getRootUrl('/find-a-couple')} className="bottom-footer-link" variant="altPurple">
        Find a Couple
      </CTALink>
    </>
  );
};

export const ClassicFooter: React.FC<{}> = () => {
  return (
    <footer>
      <div className="footer-top-logo-wrapper">
        <CTALink id="footer2brand" href={getRootUrl('/')} variant="altPurple">
          <ClassicJoyLogoSvg className="footer-joy-classic-icon" />
        </CTALink>
      </div>
      <div className="footer-menu-container">
        <Columns>
          <Columns.Column width="half">
            <div className="footer-hero-menu">
              <ClassicFooterHeroMenuItem>
                <CTALink id="footer2weddingWebsite" href={getRootUrl('/wedding-website')} variant="footerAlt">
                  Wedding Website
                </CTALink>
              </ClassicFooterHeroMenuItem>
              <ClassicFooterHeroMenuItem>
                <CTALink id="footer2guestList" href={getRootUrl('/guest-list')} variant="footerAlt">
                  Guest List
                </CTALink>
              </ClassicFooterHeroMenuItem>
              <ClassicFooterHeroMenuItem>
                <CTALink id="footer2weddingInvitations" href={getRootUrl('/wedding-invitations')} variant="footerAlt">
                  Invites & RSVP
                </CTALink>
              </ClassicFooterHeroMenuItem>
              <ClassicFooterHeroMenuItem>
                <CTALink id="footer2saveTheDate" href={getRootUrl('/save-the-date')} variant="footerAlt">
                  Save the Dates
                </CTALink>
              </ClassicFooterHeroMenuItem>
              <ClassicFooterHeroMenuItem>
                <CTALink id="footer2registry" href={getRootUrl('/registry')} variant="footerAlt">
                  Registries
                </CTALink>
              </ClassicFooterHeroMenuItem>
            </div>
            <div className="footer-app-get-started-menu">{appGetStartedMenuContent()}</div>
          </Columns.Column>
          <Columns.Column width="half">
            <Flex justifyContent={'flex-end'}>
              <div className="footer-sub-hero-menus-container">
                <div className="footer-sub-hero-menu">
                  <FooterSubHeroHeader>About Joy</FooterSubHeroHeader>
                  <FooterSubHeroItem>
                    <CTALink id="footer2pricing" variant="footer" href={getRootUrl('/pricing')}>
                      Pricing
                    </CTALink>
                  </FooterSubHeroItem>
                  <FooterSubHeroItem>
                    <CTALink id="footer2designs" variant="footer" href={getRootUrl('/designs')}>
                      Design Templates
                    </CTALink>
                  </FooterSubHeroItem>
                  <FooterSubHeroItem>
                    <CTALink id="footer2newsroom" href={getRootUrl('/newsroom')} variant="footer">
                      Newsroom
                    </CTALink>
                  </FooterSubHeroItem>
                  <FooterSubHeroItem>
                    <CTALink id="footer2careers" href={getRootUrl('/careers')} variant="footer">
                      Careers
                    </CTALink>
                  </FooterSubHeroItem>
                  <FooterSubHeroItem>
                    <CTALink id="footer2updates" href="https://updates.withjoy.com/" variant="footer">
                      Latest Updates
                    </CTALink>
                  </FooterSubHeroItem>
                </div>

                <div className="footer-sub-hero-menu footer-sub-hero-menu-resources">
                  <FooterSubHeroHeader>Resources</FooterSubHeroHeader>
                  <FooterSubHeroItem>
                    <CTALink id="footer2help" href="https://help.withjoy.com/" variant="footer">
                      Support
                    </CTALink>
                  </FooterSubHeroItem>
                  <FooterSubHeroItem>
                    <CTALink id="footer2faq" href={getRootUrl('/faq')} variant="footer">
                      FAQs
                    </CTALink>
                  </FooterSubHeroItem>
                  <FooterSubHeroItem>
                    <CTALink id="footer2demo" href={getRootUrl('/demo')} variant="footer">
                      Sample Website
                    </CTALink>
                  </FooterSubHeroItem>
                  <FooterSubHeroItem>
                    <CTALink id="footer2app" href={getRootUrl('/app')} variant="footer">
                      Mobile App
                    </CTALink>
                  </FooterSubHeroItem>
                  <FooterSubHeroItem>
                    <CTALink id="footer2privacy" href="https://privacy.withjoy.com/" variant="footer">
                      Privacy
                    </CTALink>
                  </FooterSubHeroItem>
                  <FooterSubHeroItem>
                    <CTALink id="footer2EULA" href={getRootUrl('/EULA')} variant="footer">
                      Terms of Use
                    </CTALink>
                  </FooterSubHeroItem>
                </div>

                <div className="footer-sub-hero-menu">
                  <FooterSubHeroHeader>Ideas & Inspiration</FooterSubHeroHeader>
                  <FooterSubHeroItem>
                    <CTALink id="footer2blogPlanning" href="https://withjoy.com/blog/category/wedding-planning/" variant="footer">
                      Wedding Planning
                    </CTALink>
                  </FooterSubHeroItem>
                  <FooterSubHeroItem>
                    <CTALink id="footer2blogRealWeddings" href="https://withjoy.com/blog/category/real-weddings/" variant="footer">
                      Real Weddings
                    </CTALink>
                  </FooterSubHeroItem>
                  <FooterSubHeroItem>
                    <CTALink id="footer2blogTipsAndTricks" href="https://withjoy.com/blog/category/tips-and-tricks/" variant="footer">
                      Tips and Tricks
                    </CTALink>
                  </FooterSubHeroItem>
                </div>

                <div className="social-media-menu">
                  <CTALink id="footer2instagram" href="https://www.instagram.com/withjoy" className="social-media-link" variant="footer">
                    <InstagramIcon id="instagram-icon" className="social-media-icon" />
                  </CTALink>
                  <CTALink id="footer2pinterest" href="https://www.pinterest.com/joytheapp" className="social-media-link" variant="footer">
                    <PinterestIcon id="pinterest-icon" className="social-media-icon" />
                  </CTALink>
                  <CTALink id="footer2facebook" href="https://www.facebook.com/joytheapp" className="social-media-link" variant="footer">
                    <FacebookIcon id="facebook-icon" className="social-media-icon" />
                  </CTALink>
                  <CTALink
                    id="footer2twitter"
                    href="https://twitter.com/joytheapp"
                    className="social-media-link twitter-link"
                    variant="footer"
                  >
                    #withjoy
                  </CTALink>
                </div>
              </div>
            </Flex>
          </Columns.Column>
        </Columns>
      </div>

      <div className="footer-bottom-menu">
        <Columns>
          <Columns.Column width={8}>
            <div className="footer-bottom-menu-left">{appGetStartedMenuContent()}</div>
          </Columns.Column>
          <Columns.Column width={4}>
            <div className="footer-bottom-menu-right">
              <CTALink id="footer2brand" href={getRootUrl('/')} variant="altPurple">
                <ClassicJoyLogoSvg className="footer-joy-classic-icon" />
              </CTALink>
            </div>
          </Columns.Column>
        </Columns>
      </div>
    </footer>
  );
};
